<script setup lang="ts">
import { computed } from 'vue';
import GaiaIconButton from '../base/GaiaIconButton.vue';
import GaiaText from '../base/GaiaText.vue';
import GaiaSelect from '../base/selects/GaiaSelect.vue';
import {
  gaiaFontSize,
  gaiaFontWeight,
  gaiaIconButtonOverrides,
  gaiaNeutral,
} from '../themes/tokens';

const props = defineProps({
  page: {
    type: Number,
    required: true,
  },
  pageSize: {
    type: Number,
    required: true,
  },
  /** Total matching rows, not the number on this page. */
  total: {
    type: Number,
    required: true,
  },
  pageSizes: {
    type: Array as () => number[],
    default: () => [5, 10, 25, 50],
  },
});

const emit = defineEmits<{ 'update:page': [page: number]; 'update:pageSize': [size: number] }>();

const totalPages = computed(() => Math.max(1, Math.ceil(props.total / props.pageSize)));
const start = computed(() => (props.page - 1) * props.pageSize);

/** "11–20 of 34" — the range this page covers, tabular so it doesn't jitter. */
const rangeLabel = computed(() =>
  props.total
    ? `${start.value + 1}–${Math.min(start.value + props.pageSize, props.total)} of ${props.total}`
    : '0 of 0'
);

/**
 * The page buttons, windowed: past 7 pages the row would grow without bound
 * (an obligations list paginates into the dozens), so the strip keeps a
 * constant 7 slots — first and last always visible, a window around the
 * current page, and `…` where pages are elided. Strings are the gaps.
 */
const pages = computed<(number | 'gap-left' | 'gap-right')[]>(() => {
  const last = totalPages.value;
  if (last <= 7) return Array.from({ length: last }, (_, index) => index + 1);
  if (props.page <= 4) return [1, 2, 3, 4, 5, 'gap-right', last];
  if (props.page >= last - 3) return [1, 'gap-left', last - 4, last - 3, last - 2, last - 1, last];
  return [1, 'gap-left', props.page - 1, props.page, props.page + 1, 'gap-right', last];
});

const sizeOptions = computed(() =>
  props.pageSizes.map(size => ({ label: String(size), value: size }))
);

const border = gaiaNeutral.borderSoft;
const pageBorder = 'rgba(25,27,31,.1)';
const ink = gaiaNeutral.ink;
const surface = gaiaNeutral.surface;
const currentFill = gaiaNeutral.surfaceMuted;
const tertiary = gaiaNeutral.textTertiary;
const pageSizeText = gaiaFontSize.micro;
const pageWeight = String(gaiaFontWeight.bold);
const iconOverrides = gaiaIconButtonOverrides;
</script>

<template>
  <!-- The table's footer strip: range on the left, rows-per-page and the page
       buttons on the right. Naive's n-pagination draws its own shell and can't
       be retuned to the mock's 28px squares, so this is bespoke on the tokens.
       Sits in GaiaDataTable's `#footer`, which supplies the strip background. -->
  <n-flex align="center" :size="16" :wrap="false" :style="{ height: '48px', padding: '0 18px' }">
    <!-- flex none + nowrap: the label must never be the thing that gives way
         if the strip is ever tight — a wrapped "1– / 10 / of" is unreadable. -->
    <gaia-text
      size="micro"
      tone="subtle"
      :style="{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', flex: 'none' }"
    >
      {{ rangeLabel }}
    </gaia-text>

    <n-flex align="center" :size="14" :wrap="false" :style="{ marginLeft: 'auto' }">
      <!-- No sizes offered means no control: a caller that fixes the page size
           (a picker inside a modal) would otherwise get an empty select. -->
      <n-flex v-if="sizeOptions.length > 0" align="center" :size="8" :wrap="false">
        <gaia-text size="micro" tone="subtle">Rows</gaia-text>
        <gaia-select
          :value="props.pageSize"
          :options="sizeOptions"
          size="tiny"
          :consistent-menu-width="false"
          :style="{ width: '68px' }"
          @update:value="(size: number) => emit('update:pageSize', size)"
        />
      </n-flex>

      <n-flex align="center" :size="4" :wrap="false">
        <gaia-icon-button
          :dimension="28"
          :disabled="props.page <= 1"
          :theme-overrides="iconOverrides"
          title="Previous page"
          @click="emit('update:page', props.page - 1)"
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
          >
            <path d="M7.5 2.5 4 6l3.5 3.5" />
          </svg>
        </gaia-icon-button>

        <template v-for="entry in pages" :key="entry">
          <span v-if="typeof entry === 'string'" class="gap" aria-hidden="true">…</span>
          <button
            v-else
            type="button"
            class="page"
            :class="{ current: entry === props.page }"
            @click="emit('update:page', entry)"
          >
            {{ entry }}
          </button>
        </template>

        <gaia-icon-button
          :dimension="28"
          :disabled="props.page >= totalPages"
          :theme-overrides="iconOverrides"
          title="Next page"
          @click="emit('update:page', props.page + 1)"
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
          >
            <path d="M4.5 2.5 8 6l-3.5 3.5" />
          </svg>
        </gaia-icon-button>
      </n-flex>
    </n-flex>
  </n-flex>
</template>

<style scoped>
/* A page number is a square; the current one keeps its ink number on a muted
   fill with an ink border. n-button can't retune its border and background
   together at this size without four overrides. */
.page {
  min-width: 28px;
  height: 28px;
  padding: 0 7px;
  border: 1px solid v-bind(pageBorder);
  border-radius: 8px;
  background: v-bind(surface);
  font-family: inherit;
  font-size: v-bind(pageSizeText);
  font-weight: v-bind(pageWeight);
  color: v-bind(tertiary);
  cursor: pointer;
}

.page:hover {
  border-color: v-bind(border);
  color: v-bind(ink);
}

.page.current {
  border-color: v-bind(ink);
  background: v-bind(currentFill);
  color: v-bind(ink);
}

/* An elision, not a control — no border, no hover, not clickable. */
.gap {
  min-width: 20px;
  text-align: center;
  font-size: v-bind(pageSizeText);
  color: v-bind(tertiary);
  user-select: none;
}
</style>
